import { PALETTE } from "@/composables/chartPalette";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { goSolidApi } from "@/composables/useGoSolidApi";
import { MONTH_NAMES } from "@/constants";
import type { Company, PeriodLimit, RegistryDevice } from "@/types";
import type { StackableDataset } from "@/types/ChartData";
import { computed, ref, Ref } from "vue";
import { useI18n } from "vue-i18n";

const VIEW_MODE_NEXT_12 = "next-12-months" as const;
const REPEATED_ARRAY_PARAMS_SERIALIZER = { indexes: null };

// Allocation type configuration for data-driven operations
const ALLOCATION_TYPES = {
  ppa: {
    key: "ppa" as const,
    translationKey: "production_strategy.reserved_for_ppas",
    colors: {
      background: PALETTE.semanticOverrides.portfolioType.PPA,
      border: PALETTE.semanticOverrides.portfolioType.PPA,
    },
    hasAggregateData: true, // Has agreementCount and individual_allocations
  },
  forwardCommitted: {
    key: "forwardCommitted" as const,
    translationKey: "production_strategy.committed_forward",
    estimateWhenShared: true,
    colors: {
      background: PALETTE.semanticOverrides.portfolioType.Forward,
      border: PALETTE.semanticOverrides.portfolioType.Forward,
    },
    hasAggregateData: false,
  },
  forwardToBeSold: {
    key: "forwardToBeSold" as const,
    translationKey: "production_strategy.to_be_sold",
    descriptionKey: "production_strategy.to_be_sold_description",
    estimateWhenShared: true,
    colors: {
      background: PALETTE.semanticOverrides.portfolioType["Waiting For Action"],
      border: PALETTE.semanticOverrides.portfolioType["Waiting For Action"],
    },
    hasAggregateData: false,
  },
  selfManaged: {
    key: "selfManaged" as const,
    translationKey: "production_strategy.self_manage",
    colors: {
      background: PALETTE.semanticOverrides.portfolioType["Self-managed"],
      border: PALETTE.semanticOverrides.portfolioType["Self-managed"],
    },
    hasAggregateData: false,
  },
  spot: {
    key: "spot" as const,
    translationKey: "production_strategy.sell_all_remaining_spot",
    colors: {
      background: PALETTE.semanticOverrides.portfolioType["Spot Sales"],
      border: PALETTE.semanticOverrides.portfolioType["Spot Sales"],
    },
    hasAggregateData: false,
  },
} as const;

// Shared allocation types used by the planner and consumers
export interface PerMonthAllocation {
  monthIndex: number;
  year: number;
  label: string;
  production: number;
  forwardEligibleProduction: number;
  ppaFromForwardEligible: number;
  ppa: number;
  forwardCommitted: number;
  forwardToBeSold: number;
  selfManaged: number;
  spot: number;
}

interface AllocationPlanTotals {
  produced: number;
  ppa: number;
  forwardCommitted: number;
  forwardToBeSold: number;
  selfManaged: number;
  spot: number;
}

export interface AllocationPlanResult {
  perMonth: PerMonthAllocation[];
  totals: AllocationPlanTotals;
  settings: { selfManagedPercentage: number; spotPercentage: number };
  allocations: AllocationData;
}

export interface PPAIndividualAllocation {
  portfolio_id: number;
  portfolio_name: string;
  yearly_allocation: number;
  yearly_percentage: number;
  allocation_type: "fixed" | "percentage" | "unknown";
  registry_device_ids: string[];
  // Only one of the two is filled: limits for a fixed allocation, allocate_percentage for a
  // percentage one. The other comes back null.
  limits: PeriodLimit[] | null;
  allocate_percentage: number | null;
}

export interface AllocationData {
  ppa: {
    amount: number;
    percentage: number;
    agreementCount?: number;
    individual_allocations?: PPAIndividualAllocation[];
  };
  forward: {
    amount: number;
    percentage: number;
    committedAmount?: number;
    upcomingAmount?: number;
    percentagePerQuarter?: number;
    /**
     * The company is in a forward pool with other members, so its share of that pool's committed and
     * listed volume is apportioned from the members' production estimates rather than contracted.
     */
    forwardIsEstimate: boolean;
  };
  forwardCommitted: {
    amount: number;
    percentage: number;
  };
  forwardToBeSold: {
    amount: number;
    percentage: number;
  };
  selfManaged: {
    amount: number;
    percentage: number;
  };
  spot: {
    amount: number;
    percentage: number;
  };
}

export interface MonthPoint {
  monthIndex: number;
  year: number;
}

export interface Colors {
  background: string;
  border: string;
}

export interface ChartData {
  labels: string[];
  months: MonthPoint[];
  datasets: StackableDataset[];
}

export function useProductionAllocationData(
  companies: Readonly<Ref<Company[]>> | Company[],
  viewMode: Ref<"next-12-months" | "next-year">,
  _registryDevices?: Ref<RegistryDevice[]> | RegistryDevice[]
) {
  const { globalErrorHandler } = useGlobalHandlers();
  const { t } = useI18n();
  const isLoading = ref(false);
  const loadAttempted = ref(false);
  /** The first load is in flight: a later refresh keeps what the previous one rendered. */
  const isInitialLoad = computed(() => isLoading.value && !loadAttempted.value);
  const plan = ref<AllocationPlanResult | null>(null);
  const companyResults = ref<AllocationPlanResult[]>([]);

  // Convert to reactive ref if needed
  const reactiveCompanies = Array.isArray(companies)
    ? ref(companies)
    : companies;

  const filteredPlan = computed(() => {
    if (!plan.value) return null;
    return _filterPlanByViewMode(plan.value, viewMode.value);
  });

  const filteredCompanyResults = computed(() =>
    companyResults.value.map((result) =>
      _filterPlanByViewMode(result, viewMode.value)
    )
  );

  const allocations = computed(() =>
    _createDefaultAllocations(filteredPlan.value?.allocations)
  );
  const totalProduction = computed(
    () => filteredPlan.value?.totals.produced ?? 0
  );
  const chartData = computed<ChartData>(() =>
    buildChartData(filteredPlan.value, viewMode.value)
  );

  const refreshData = _createMultiCompanyRefreshFunction();

  return {
    isLoading,
    isInitialLoad,
    allocations,
    chartData,
    totalProduction,
    refreshData,
    filteredCompanyResults,
  };

  function _createDefaultAllocations(planAllocations?: AllocationData) {
    return (
      planAllocations || {
        ppa: { amount: 0, percentage: 0 },
        forward: { amount: 0, percentage: 0, forwardIsEstimate: false },
        forwardCommitted: { amount: 0, percentage: 0 },
        forwardToBeSold: { amount: 0, percentage: 0 },
        selfManaged: { amount: 0, percentage: 0 },
        spot: { amount: 0, percentage: 0 },
      }
    );
  }

  function buildChartData(
    planValue: AllocationPlanResult | null,
    currentViewMode: string
  ): ChartData {
    if (!planValue) return { labels: [], months: [], datasets: [] };

    const currentYear = new Date().getFullYear();
    const labels = planValue.perMonth.map((m) => {
      const base = MONTH_NAMES[m.monthIndex - 1];
      return currentViewMode === VIEW_MODE_NEXT_12 && m.year !== currentYear
        ? `${base} ${m.year}`
        : base;
    });
    const months = planValue.perMonth.map((m) => ({
      monthIndex: m.monthIndex,
      year: m.year,
    }));

    return {
      labels,
      months,
      datasets: createChartDatasets(planValue),
    };
  }

  function createChartDatasets(planValue: AllocationPlanResult) {
    const isEstimate = planValue.allocations.forward.forwardIsEstimate;
    return Object.values(ALLOCATION_TYPES)
      .filter(
        (allocationType) => planValue.allocations[allocationType.key].amount > 0
      )
      .map((allocationType) => ({
        ...createDataset(
          datasetLabel(allocationType, isEstimate),
          planValue.perMonth.map((m) => m[allocationType.key]),
          allocationType.colors
        ),
        ...datasetDescription(allocationType),
      }));
  }

  /**
   * The to-be-sold series is a placement, not a plan: listed volume not yet sold, put where the
   * months still have production after the commitments. The tooltip says so, so a month's figure is
   * not read as volume promised for that month.
   */
  function datasetDescription(
    allocationType: (typeof ALLOCATION_TYPES)[keyof typeof ALLOCATION_TYPES]
  ) {
    return "descriptionKey" in allocationType
      ? { description: t(allocationType.descriptionKey) }
      : {};
  }

  /**
   * A forward series drawn from a shared pool is this company's apportioned share, not a contracted
   * quota, so the tooltip says so rather than letting the number read as a promise.
   */
  function datasetLabel(
    allocationType: { translationKey: string; estimateWhenShared?: boolean },
    isEstimate: boolean
  ) {
    const label = t(allocationType.translationKey);
    return allocationType.estimateWhenShared === true && isEstimate
      ? `${label} (${t("production_strategy.forward_estimate")})`
      : label;
  }

  function createDataset(label: string, data: number[], colors: Colors) {
    return {
      label,
      data,
      backgroundColor: colors.background,
      borderColor: colors.border,
      borderWidth: 1,
    };
  }

  function _createMultiCompanyRefreshFunction() {
    return async () => {
      try {
        isLoading.value = true;
        const companyIds = reactiveCompanies.value
          .map((company) => company.id)
          .filter((id): id is number => typeof id === "number");

        if (companyIds.length === 0) {
          companyResults.value = [];
          plan.value = _createEmptyAllocationResult();
          return;
        }

        if (companyIds.length === 1) {
          const response = await goSolidApi.get(
            `/api/company/${companyIds[0]}/allocation-simulation`,
            { params: { view_mode: VIEW_MODE_NEXT_12 } }
          );
          const result = response.data as AllocationPlanResult;
          companyResults.value = [result];
          plan.value = result;
          return;
        }

        const response = await goSolidApi.get(
          "/api/companies/allocation-simulations",
          {
            params: {
              view_mode: VIEW_MODE_NEXT_12,
              company_ids: companyIds,
            },
            paramsSerializer: REPEATED_ARRAY_PARAMS_SERIALIZER,
          }
        );
        const simulations = response.data.simulations ?? {};
        const emptyResult = _createEmptyAllocationResult();
        // Preserve company order: companyResults[i] must match companies[i]
        const results = companyIds.map(
          (id) =>
            (simulations[String(id)] as AllocationPlanResult | undefined) ??
            emptyResult
        );

        companyResults.value = results;

        // Aggregate all company results into a single plan
        plan.value = _aggregateCompanyResults(results);
      } catch (error) {
        globalErrorHandler?.(error);
        throw new Error(
          `Failed to load allocation data for consolidated view: ${
            error instanceof Error ? error.message : String(error)
          }`,
          { cause: error }
        );
      } finally {
        isLoading.value = false;
        loadAttempted.value = true;
      }
    };
  }

  function _createEmptyAllocationResult(): AllocationPlanResult {
    // Create empty totals structure
    const emptyTotals: AllocationPlanTotals = {
      produced: 0,
    } as AllocationPlanTotals;

    // Initialize all allocation types to 0
    Object.keys(ALLOCATION_TYPES).forEach((key) => {
      (emptyTotals as any)[key] = 0;
    });

    // Create empty allocations structure
    const emptyAllocations: AllocationData = {} as AllocationData;

    Object.entries(ALLOCATION_TYPES).forEach(([key]) => {
      (emptyAllocations as any)[key] = {
        amount: 0,
        percentage: 0,
      };
    });

    // Add PPA-specific empty data
    emptyAllocations.ppa.agreementCount = 0;
    emptyAllocations.ppa.individual_allocations = [];

    // Add forward-specific empty data
    emptyAllocations.forward = {
      amount: 0,
      percentage: 0,
      committedAmount: 0,
      upcomingAmount: 0,
      percentagePerQuarter: 0,
      forwardIsEstimate: false,
    };

    return {
      perMonth: [],
      totals: emptyTotals,
      settings: { selfManagedPercentage: 0, spotPercentage: 0 },
      allocations: emptyAllocations,
    };
  }

  function _aggregateCompanyResults(
    results: AllocationPlanResult[]
  ): AllocationPlanResult {
    if (results.length === 0) {
      return _createEmptyAllocationResult();
    }

    if (results.length === 1) {
      // Single company - return as-is
      return results[0];
    }

    // Multi-company aggregation
    const aggregatedPerMonth = _aggregateProductionTotals(results);
    const aggregatedTotals = _calculateTotalsFromMonthly(aggregatedPerMonth);
    const aggregatedAllocations = _aggregateAllocations(
      results,
      aggregatedTotals
    );
    const aggregatedSettings = _aggregateSettings(results, aggregatedTotals);

    return {
      perMonth: aggregatedPerMonth,
      totals: aggregatedTotals,
      settings: aggregatedSettings,
      allocations: aggregatedAllocations,
    };
  }

  function _aggregateProductionTotals(
    results: AllocationPlanResult[]
  ): PerMonthAllocation[] {
    // Group by month/year and sum production values
    const monthMap = new Map<string, PerMonthAllocation>();

    results.forEach((result) => {
      result.perMonth.forEach((month) => {
        const key = `${month.year}-${month.monthIndex}`;
        if (!monthMap.has(key)) {
          // Create initial month structure with all allocation types set to 0
          const initialMonth: PerMonthAllocation = {
            monthIndex: month.monthIndex,
            year: month.year,
            label: month.label,
            production: 0,
            forwardEligibleProduction: 0,
            ppaFromForwardEligible: 0,
          } as PerMonthAllocation;

          // Initialize all allocation types to 0
          Object.keys(ALLOCATION_TYPES).forEach((key) => {
            (initialMonth as any)[key] = 0;
          });

          monthMap.set(key, initialMonth);
        }

        const existing = monthMap.get(key);
        if (existing) {
          existing.production += month.production;
          existing.forwardEligibleProduction +=
            month.forwardEligibleProduction ?? 0;
          existing.ppaFromForwardEligible += month.ppaFromForwardEligible ?? 0;

          // Sum all allocation types dynamically
          Object.keys(ALLOCATION_TYPES).forEach((allocationType) => {
            (existing as any)[allocationType] += (month as any)[allocationType];
          });
        }
      });
    });

    return Array.from(monthMap.values()).sort((a, b) => {
      if (a.year !== b.year) return a.year - b.year;
      return a.monthIndex - b.monthIndex;
    });
  }

  function _calculateTotalsFromMonthly(
    perMonth: PerMonthAllocation[]
  ): AllocationPlanTotals {
    // Create initial totals structure
    const initialTotals: AllocationPlanTotals = {
      produced: 0,
    } as AllocationPlanTotals;

    // Initialize all allocation types to 0
    Object.keys(ALLOCATION_TYPES).forEach((key) => {
      (initialTotals as any)[key] = 0;
    });

    return perMonth.reduce((totals, month) => {
      const newTotals = { ...totals };
      newTotals.produced += month.production;
      (newTotals as any).forwardEligibleProduction =
        ((newTotals as any).forwardEligibleProduction || 0) +
        (month.forwardEligibleProduction ?? 0);
      (newTotals as any).ppaFromForwardEligible =
        ((newTotals as any).ppaFromForwardEligible || 0) +
        (month.ppaFromForwardEligible ?? 0);

      // Sum all allocation types dynamically
      Object.keys(ALLOCATION_TYPES).forEach((allocationType) => {
        (newTotals as any)[allocationType] += (month as any)[allocationType];
      });

      return newTotals;
    }, initialTotals);
  }

  // Helper function to calculate allocation percentage
  function _calculatePercentage(amount: number, totalProduced: number): number {
    return totalProduced > 0 ? (amount / totalProduced) * 100 : 0;
  }

  function _aggregateAllocations(
    results: AllocationPlanResult[],
    totals: AllocationPlanTotals
  ): AllocationData {
    const totalProduced = totals.produced;

    // Sum PPA agreement counts and individual allocations (special case for PPA)
    const ppaAgreementCount = results.reduce(
      (sum, result) => sum + (result.allocations.ppa.agreementCount || 0),
      0
    );

    const allIndividualAllocations = results.flatMap(
      (result) => result.allocations.ppa.individual_allocations || []
    );

    // Create base allocations for all standard types
    const allocations: AllocationData = {} as AllocationData;

    Object.entries(ALLOCATION_TYPES).forEach(([key]) => {
      const amount = (totals as any)[key];
      (allocations as any)[key] = {
        amount,
        percentage: _calculatePercentage(amount, totalProduced),
      };
    });

    // Add PPA-specific data
    allocations.ppa.agreementCount = ppaAgreementCount;
    allocations.ppa.individual_allocations = allIndividualAllocations;

    // Add forward-specific aggregated data
    const forwardTotal = totals.forwardCommitted + totals.forwardToBeSold;
    allocations.forward = {
      amount: forwardTotal,
      percentage: _calculatePercentage(forwardTotal, totalProduced),
      committedAmount: totals.forwardCommitted,
      upcomingAmount: totals.forwardToBeSold,
      percentagePerQuarter:
        totalProduced > 0
          ? Math.round((forwardTotal / 4 / (totalProduced / 4)) * 100)
          : 0,
      // One shared pool anywhere in the consolidated view makes the whole forward stack an estimate.
      forwardIsEstimate: results.some(
        (result) => result.allocations.forward.forwardIsEstimate
      ),
    };

    return allocations;
  }

  function _aggregateSettings(
    results: AllocationPlanResult[],
    totals: AllocationPlanTotals
  ): { selfManagedPercentage: number; spotPercentage: number } {
    return {
      selfManagedPercentage: _calculatePercentage(
        totals.selfManaged,
        totals.produced
      ),
      spotPercentage: _calculatePercentage(totals.spot, totals.produced),
    };
  }

  function _filterPlanByViewMode(
    planValue: AllocationPlanResult,
    currentViewMode: string
  ): AllocationPlanResult {
    const monthsByKey = new Map(
      planValue.perMonth.map((m) => [`${m.year}-${m.monthIndex}`, m])
    );
    const filteredMonths = _buildMonthAxis(currentViewMode).map(
      ({ monthIndex, year }) =>
        monthsByKey.get(`${year}-${monthIndex}`) ??
        _createEmptyMonth(monthIndex, year)
    );

    // Recalculate totals from filtered months using existing function
    const filteredTotals = _calculateTotalsFromMonthly(filteredMonths);

    // Recalculate allocations from filtered totals using existing logic
    const filteredAllocations: AllocationData = {} as AllocationData;

    // Create base allocations for all standard types
    Object.entries(ALLOCATION_TYPES).forEach(([key]) => {
      const amount = (filteredTotals as any)[key];
      (filteredAllocations as any)[key] = {
        amount,
        percentage: _calculatePercentage(amount, filteredTotals.produced),
      };
    });

    // Add PPA-specific data from original plan (unchanged by filtering)
    filteredAllocations.ppa.agreementCount =
      planValue.allocations.ppa.agreementCount;
    filteredAllocations.ppa.individual_allocations =
      planValue.allocations.ppa.individual_allocations;

    // Add forward-specific aggregated data
    const forwardTotal =
      filteredTotals.forwardCommitted + filteredTotals.forwardToBeSold;
    filteredAllocations.forward = {
      amount: forwardTotal,
      percentage: _calculatePercentage(forwardTotal, filteredTotals.produced),
      committedAmount: filteredTotals.forwardCommitted,
      upcomingAmount: filteredTotals.forwardToBeSold,
      percentagePerQuarter: planValue.allocations.forward.percentagePerQuarter,
      forwardIsEstimate: planValue.allocations.forward.forwardIsEstimate,
    };

    return {
      perMonth: filteredMonths,
      totals: filteredTotals,
      settings: planValue.settings,
      allocations: filteredAllocations,
    };
  }

  function _buildMonthAxis(currentViewMode: string): MonthPoint[] {
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1;

    if (currentViewMode === VIEW_MODE_NEXT_12) {
      return Array.from({ length: 12 }, (_, i) => ({
        monthIndex: ((currentMonth - 1 + i) % 12) + 1,
        year: currentYear + Math.floor((currentMonth - 1 + i) / 12),
      }));
    }
    return Array.from({ length: 12 }, (_, i) => ({
      monthIndex: i + 1,
      year: currentYear + 1,
    }));
  }

  function _createEmptyMonth(
    monthIndex: number,
    year: number
  ): PerMonthAllocation {
    const month = {
      monthIndex,
      year,
      label: MONTH_NAMES[monthIndex - 1],
      production: 0,
      forwardEligibleProduction: 0,
      ppaFromForwardEligible: 0,
    } as PerMonthAllocation;

    Object.keys(ALLOCATION_TYPES).forEach((key) => {
      (month as any)[key] = 0;
    });

    return month;
  }
}
